import React, { useState } from 'react';
import { Clock, Sparkles, Check, ChevronRight, Scissors, Star, Calendar, Search, ShieldCheck, HeartHandshake, Coffee, Tag, UserCheck } from 'lucide-react';
import { services, serviceCategories, genderGroups } from '../data/services';
import { salonInfo } from '../data/salonInfo';
import { useLanguage } from '../context/LanguageContext';

export default function Services({ onSelectService, onOpenBooking }) {
  const { lang, t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeGender, setActiveGender] = useState('all'); // 'all' | 'women' | 'men'
  const [selectedCatId, setSelectedCatId] = useState('all');

  // Handle Gender Change
  const handleGenderChange = (gender) => {
    setActiveGender(gender);
    setSelectedCatId('all');
  };

  // Categories filtered by gender
  const availableCategories = activeGender === 'all'
    ? serviceCategories
    : serviceCategories.filter(c => c.gender === activeGender);

  // Displayed categories (gender + category filter)
  const displayedCategories = selectedCatId === 'all'
    ? availableCategories
    : availableCategories.filter(c => c.id === selectedCatId);

  // Group services by category
  const getCategoryServices = (catId) => {
    let list = services.filter(s => s.category === catId);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s => {
        const name = typeof s.name === 'object' ? (s.name[lang] || s.name.tr || s.name.nl) : s.name;
        const desc = typeof s.description === 'object' ? (s.description[lang] || s.description.tr || s.description.nl) : s.description;
        return name.toLowerCase().includes(q) || desc.toLowerCase().includes(q);
      });
    }
    return list;
  };

  const getHeaderColor = (cat) => {
    const isWomen = cat.gender === 'women';
    switch (cat.id) {
      case 'women_cut':
        return {
          headerBg: 'bg-gradient-to-r from-rose-700 to-rose-600',
          headerText: 'text-white',
          genderTag: lang === 'nl' ? '👩 DAMES' : lang === 'tr' ? '👩 KADIN' : '👩 WOMEN',
          tagBg: 'bg-rose-900/40 text-rose-100 border-rose-400/30'
        };
      case 'hair_coloring':
        return {
          headerBg: 'bg-gradient-to-r from-amber-700 to-amber-600',
          headerText: 'text-white',
          genderTag: lang === 'nl' ? '👩 DAMES' : lang === 'tr' ? '👩 KADIN' : '👩 WOMEN',
          tagBg: 'bg-amber-900/40 text-amber-100 border-amber-400/30'
        };
      case 'highlight_lowlight':
        return {
          headerBg: 'bg-gradient-to-r from-amber-600 to-yellow-600',
          headerText: 'text-white',
          genderTag: lang === 'nl' ? '👩 DAMES' : lang === 'tr' ? '👩 KADIN' : '👩 WOMEN',
          tagBg: 'bg-yellow-900/40 text-yellow-100 border-yellow-400/30'
        };
      case 'keratin_perm':
        return {
          headerBg: 'bg-gradient-to-r from-emerald-700 to-emerald-600',
          headerText: 'text-white',
          genderTag: lang === 'nl' ? '👩 DAMES' : lang === 'tr' ? '👩 KADIN' : '👩 WOMEN',
          tagBg: 'bg-emerald-900/40 text-emerald-100 border-emerald-400/30'
        };
      case 'bridal_updo':
        return {
          headerBg: 'bg-gradient-to-r from-purple-700 to-purple-600',
          headerText: 'text-white',
          genderTag: lang === 'nl' ? '👩 DAMES' : lang === 'tr' ? '👩 KADIN' : '👩 WOMEN',
          tagBg: 'bg-purple-900/40 text-purple-100 border-purple-400/30'
        };
      case 'hair_styling':
        return {
          headerBg: 'bg-gradient-to-r from-indigo-700 to-indigo-600',
          headerText: 'text-white',
          genderTag: lang === 'nl' ? '👩 DAMES' : lang === 'tr' ? '👩 KADIN' : '👩 WOMEN',
          tagBg: 'bg-indigo-900/40 text-indigo-100 border-indigo-400/30'
        };
      case 'brows_face_wax':
        return {
          headerBg: 'bg-gradient-to-r from-pink-700 to-pink-600',
          headerText: 'text-white',
          genderTag: lang === 'nl' ? '👩 DAMES' : lang === 'tr' ? '👩 KADIN' : '👩 WOMEN',
          tagBg: 'bg-pink-900/40 text-pink-100 border-pink-400/30'
        };
      case 'women_perm':
        return {
          headerBg: 'bg-gradient-to-r from-teal-700 to-teal-600',
          headerText: 'text-white',
          genderTag: lang === 'nl' ? '👩 DAMES' : lang === 'tr' ? '👩 KADIN' : '👩 WOMEN',
          tagBg: 'bg-teal-900/40 text-teal-100 border-teal-400/30'
        };
      case 'men_hair_beard':
        return {
          headerBg: 'bg-gradient-to-r from-slate-900 to-slate-800',
          headerText: 'text-white',
          genderTag: lang === 'nl' ? '👨 HEREN' : lang === 'tr' ? '👨 ERKEK' : '👨 MEN',
          tagBg: 'bg-slate-700/50 text-slate-200 border-slate-600'
        };
      case 'men_care_wax':
        return {
          headerBg: 'bg-gradient-to-r from-teal-800 to-teal-700',
          headerText: 'text-white',
          genderTag: lang === 'nl' ? '👨 HEREN' : lang === 'tr' ? '👨 ERKEK' : '👨 MEN',
          tagBg: 'bg-teal-900/50 text-teal-100 border-teal-500/30'
        };
      case 'mens_hairpiece':
      default:
        return {
          headerBg: 'bg-gradient-to-r from-slate-950 via-slate-900 to-amber-950',
          headerText: 'text-amber-300',
          genderTag: lang === 'nl' ? '👨 HEREN' : lang === 'tr' ? '👨 ERKEK' : '👨 MEN',
          tagBg: 'bg-amber-500/20 text-amber-300 border-amber-400/30'
        };
    }
  };

  const womenCount = services.filter(s => s.gender === 'women').length;
  const menCount = services.filter(s => s.gender === 'men').length;

  return (
    <section id="hizmetler" className="py-16 sm:py-24 relative bg-stone-50/70 border-t border-b border-stone-200">
      
      {/* Background Subtle Luxury Ambience */}
      <div className="absolute top-0 right-1/4 w-96 h-96 glow-aurora-amber pointer-events-none opacity-20" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 glow-aurora-emerald pointer-events-none opacity-20" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10 space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-amber-300 text-amber-950 text-xs font-black uppercase tracking-wider shadow-xs">
            <Sparkles className="w-4 h-4 text-amber-600" />
            <span>{lang === 'nl' ? 'AURA HAIR ROTTERDAM • TARIEVEN' : lang === 'tr' ? 'AURA HAIR ROTTERDAM • FİYAT LİSTESİ' : 'AURA HAIR ROTTERDAM • PRICING'}</span>
          </div>

          <h2 className="font-serif text-3xl sm:text-5xl font-black text-slate-900 tracking-tight uppercase">
            {lang === 'nl' ? 'Onze Tarieven & Prijslijst' : lang === 'tr' ? 'KADIN & ERKEK FİYAT LİSTESİ' : 'SERVICES & PRICING'}
          </h2>

          <p className="text-sm sm:text-base text-stone-600 font-medium">
            {lang === 'nl' 
              ? 'Duidelijk gescheiden tarieven voor dames en heren. Transparante prijzen zonder verrassingen in Rotterdam.'
              : lang === 'tr'
              ? 'Kadınlar ve erkekler için özenle ayrılmış resmi hizmet kataloğumuz. Sürprizsiz şeffaf fiyatlar.'
              : 'Clearly separated services and pricing for women and men. Transparent pricing with zero hidden fees.'}
          </p>

          {/* Quick Search Bar */}
          <div className="pt-2 max-w-md mx-auto">
            <div className="relative">
              <Search className="w-4 h-4 text-stone-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={lang === 'nl' ? 'Zoek een dienst (bijv. knippen, botox, pruik, harsen)...' : lang === 'tr' ? 'Hizmet ara (örn. kesim, boya, botoks, peruk, ağda)...' : 'Search service (e.g. cut, color, wig, wax)...'}
                className="w-full pl-11 pr-4 py-2.5 rounded-2xl bg-white border border-stone-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-xs sm:text-sm font-medium outline-none shadow-xs transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-stone-400 hover:text-stone-700 px-2 py-1"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 🚻 MAIN GENDER SEPARATION SWITCHER (KADINLAR / ERKEKLER / TÜMÜ) */}
        <div className="max-w-xl mx-auto mb-6 p-1.5 rounded-2xl bg-stone-200/80 border border-stone-300 shadow-inner grid grid-cols-3 gap-1.5">
          
          {/* Kadınlar Tab */}
          <button
            onClick={() => handleGenderChange('women')}
            className={`py-3 px-3 rounded-xl font-serif text-xs sm:text-sm font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs ${
              activeGender === 'women'
                ? 'bg-gradient-to-r from-rose-600 to-pink-600 text-white shadow-md scale-[1.02]'
                : 'bg-transparent text-stone-700 hover:bg-white/60'
            }`}
          >
            <span>👩</span>
            <span>{lang === 'nl' ? 'Dames' : lang === 'tr' ? 'Kadınlar' : 'Women'}</span>
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${activeGender === 'women' ? 'bg-white/25 text-white' : 'bg-stone-300/80 text-stone-700'}`}>
              {womenCount}
            </span>
          </button>

          {/* Erkekler Tab */}
          <button
            onClick={() => handleGenderChange('men')}
            className={`py-3 px-3 rounded-xl font-serif text-xs sm:text-sm font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs ${
              activeGender === 'men'
                ? 'bg-gradient-to-r from-slate-900 to-slate-800 text-white shadow-md scale-[1.02]'
                : 'bg-transparent text-stone-700 hover:bg-white/60'
            }`}
          >
            <span>👨</span>
            <span>{lang === 'nl' ? 'Heren' : lang === 'tr' ? 'Erkekler' : 'Men'}</span>
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${activeGender === 'men' ? 'bg-white/25 text-white' : 'bg-stone-300/80 text-stone-700'}`}>
              {menCount}
            </span>
          </button>

          {/* Tümü Tab */}
          <button
            onClick={() => handleGenderChange('all')}
            className={`py-3 px-3 rounded-xl font-serif text-xs sm:text-sm font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs ${
              activeGender === 'all'
                ? 'bg-amber-600 text-white shadow-md scale-[1.02]'
                : 'bg-transparent text-stone-700 hover:bg-white/60'
            }`}
          >
            <span>✨</span>
            <span>{lang === 'nl' ? 'Alles' : lang === 'tr' ? 'Tümü' : 'All'}</span>
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${activeGender === 'all' ? 'bg-white/25 text-white' : 'bg-stone-300/80 text-stone-700'}`}>
              {services.length}
            </span>
          </button>

        </div>

        {/* 🏷️ Horizontal Sub-Category Filter Pills */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-8 max-w-5xl mx-auto px-2">
          <button
            onClick={() => setSelectedCatId('all')}
            className={`px-4 py-2 rounded-full text-xs font-bold tracking-wider whitespace-nowrap shrink-0 transition-all cursor-pointer shadow-xs ${
              selectedCatId === 'all'
                ? 'bg-slate-900 text-white shadow-sm font-black scale-105'
                : 'bg-white text-slate-700 hover:bg-stone-100 border border-stone-200'
            }`}
          >
            {activeGender === 'women'
              ? (lang === 'nl' ? 'Alle Dames Categorieën' : lang === 'tr' ? 'Tüm Kadın Kategorileri' : 'All Women Categories')
              : activeGender === 'men'
              ? (lang === 'nl' ? 'Alle Heren Categorieën' : lang === 'tr' ? 'Tüm Erkek Kategorileri' : 'All Men Categories')
              : (lang === 'nl' ? 'Alle Categorieën' : lang === 'tr' ? 'Tüm Kategoriler' : 'All Categories')}
          </button>

          {availableCategories.map((cat) => {
            const isSelected = selectedCatId === cat.id;
            const catName = cat.name[lang] || cat.name.tr || cat.name.nl;
            const count = services.filter(s => s.category === cat.id).length;

            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCatId(cat.id)}
                className={`px-4 py-2 rounded-full text-xs font-bold tracking-wider whitespace-nowrap shrink-0 transition-all cursor-pointer shadow-xs flex items-center gap-1.5 ${
                  isSelected
                    ? 'bg-amber-600 text-white font-black shadow-md scale-105'
                    : 'bg-white text-slate-700 hover:bg-amber-50 border border-stone-200'
                }`}
              >
                <span>{catName}</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${isSelected ? 'bg-black/20 text-white' : 'bg-stone-100 text-stone-600'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* 📋 RESPONSIVE PRICE BOARD MATRIX */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${displayedCategories.length >= 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2 max-w-4xl mx-auto'} gap-6 items-start`}>
          {displayedCategories.map((cat) => {
            const catServices = getCategoryServices(cat.id);
            const style = getHeaderColor(cat);
            const catTitle = cat.name[lang] || cat.name.tr || cat.name.nl;

            if (catServices.length === 0 && searchQuery.trim()) {
              return null;
            }

            return (
              <div
                key={cat.id}
                className="flex flex-col rounded-2xl overflow-hidden bg-white border border-stone-200 shadow-sm hover:shadow-xl transition-all duration-300"
              >
                {/* Column Top Header Pill / Box with Gender Tag */}
                <div className={`${style.headerBg} ${style.headerText} p-4 shadow-xs flex items-center justify-between`}>
                  <div>
                    <span className={`inline-block text-[10px] uppercase font-black tracking-widest px-2 py-0.5 rounded-full border mb-1 ${style.tagBg}`}>
                      {style.genderTag}
                    </span>
                    <h3 className="font-serif text-base font-black tracking-wide uppercase">
                      {catTitle}
                    </h3>
                  </div>
                  <span className="text-xs font-black px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-xs">
                    {catServices.length} {lang === 'nl' ? 'diensten' : lang === 'tr' ? 'hizmet' : 'services'}
                  </span>
                </div>

                {/* Stacked Service Items */}
                <div className="divide-y divide-stone-100 p-2 space-y-1">
                  {catServices.length === 0 ? (
                    <div className="p-6 text-center text-xs text-stone-400 font-medium">
                      {lang === 'nl' ? 'Geen diensten gevonden' : lang === 'tr' ? 'Hizmet bulunamadı' : 'No services found'}
                    </div>
                  ) : (
                    catServices.map((service) => {
                      const sName = service.name[lang] || service.name.tr || service.name.nl;
                      const sDesc = service.description[lang] || service.description.tr || service.description.nl;
                      const isFree = service.price === 0;

                      return (
                        <div
                          key={service.id}
                          onClick={() => onSelectService ? onSelectService(service) : onOpenBooking && onOpenBooking(service)}
                          className="p-3.5 rounded-xl hover:bg-amber-50/80 border border-transparent hover:border-amber-300 transition-all duration-200 cursor-pointer group flex flex-col justify-between gap-1.5"
                        >
                          {/* Service Name & Price Row */}
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="text-[13px] font-black text-slate-800 uppercase tracking-tight group-hover:text-amber-800 transition-colors leading-snug">
                              {sName}
                            </h4>
                            <div className="shrink-0 text-right flex items-baseline gap-1.5 justify-end">
                              {service.isStartingPrice && (
                                <span className="text-[11px] font-bold text-amber-700">
                                  {lang === 'nl' ? 'Vanaf' : lang === 'tr' ? 'Başlangıç' : 'From'}
                                </span>
                              )}
                              <span className={`font-mono text-sm font-black ${isFree ? 'text-emerald-700 uppercase' : 'text-slate-900 group-hover:text-amber-700'}`}>
                                {isFree 
                                  ? (lang === 'nl' ? 'GRATIS' : lang === 'tr' ? 'ÜCRETSİZ' : 'FREE') 
                                  : `${salonInfo.currency}${service.price.toFixed(2).replace('.', ',')}`}
                              </span>
                            </div>
                          </div>

                          {/* Short Description */}
                          <p className="text-[11px] text-stone-500 line-clamp-2 leading-relaxed font-normal">
                            {sDesc}
                          </p>

                          {/* Bottom Row: Duration & Booking Trigger Button */}
                          <div className="flex items-center justify-between pt-1 text-[11px] text-stone-400">
                            <span className="flex items-center gap-1 font-semibold">
                              <Clock className="w-3 h-3 text-stone-400" />
                              <span>{service.duration} {t.services.minutes || 'dk'}</span>
                            </span>

                            <span className="text-[11px] font-black text-amber-700 group-hover:underline flex items-center gap-0.5">
                              <span>{lang === 'nl' ? 'Kiezen' : lang === 'tr' ? 'Randevu Al' : 'Book'}</span>
                              <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

              </div>
            );
          })}
        </div>

        {/* Bottom CTA Banner */}
        <div className="mt-14 p-6 sm:p-8 rounded-3xl bg-slate-900 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 border border-slate-800">
          <div className="space-y-1.5 text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 text-xs font-bold uppercase">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              <span>{lang === 'nl' ? 'Snel & Eenvoudig' : lang === 'tr' ? 'Hızlı & Kolay' : 'Quick & Easy'}</span>
            </div>
            <h3 className="font-serif text-xl sm:text-2xl font-black text-white">
              {lang === 'nl' ? 'Direct een behandeling inplannen?' : lang === 'tr' ? 'İstediğiniz Hizmeti Seçip Randevunuzu Alın' : 'Ready to Book Your Service?'}
            </h3>
            <p className="text-xs sm:text-sm text-slate-300 max-w-xl">
              {lang === 'nl' 
                ? 'Kies uw gewenste tijdstip en stylist binnen 30 seconden. Geen wachttijd op afspraak.'
                : lang === 'tr'
                ? 'İstediğiniz gün ve saati seçin, randevunuz anında sisteme kaydedilsin. Sıfır bekleme garantisi.'
                : 'Select your preferred date & barber in 30 seconds with instant booking.'}
            </p>
          </div>

          <button
            onClick={() => onOpenBooking && onOpenBooking()}
            className="btn-gold-sheen w-full md:w-auto px-8 py-4 rounded-2xl bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 text-white font-extrabold text-xs sm:text-sm uppercase tracking-wider shadow-lg shadow-amber-500/30 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2.5 shrink-0 cursor-pointer"
          >
            <Calendar className="w-4 h-4" />
            <span>{lang === 'nl' ? 'Online Afspraak Maken' : lang === 'tr' ? 'Hemen Randevu Al' : 'Book Appointment Online'}</span>
          </button>
        </div>

      </div>

    </section>
  );
}


